import React, { useState } from 'react';

import { AppBottomSheet, ConfirmDialog, SheetAction } from '~/components/core';
import { Strategy } from '~/api-services/AIStrategyBuilderService/types';
import {
    apiVersionState,
    isArchivable,
    isDiscardable,
    isDisableable,
    isEnableable,
} from '~/constants/strategyConstants';
import i18n from '~/i18n';

import { useAppSelector } from '~/hooks';
import { symbolsSelector } from '~/state/selectors';

import { StrategyActionResult } from '../hooks/useStrategies';
import { useStrategyConsent } from '../hooks/useStrategyConsent';
import { strategyMarketLine } from '../utils/format';

export type StrategyActions = {
    enable: (id: string) => Promise<StrategyActionResult>;
    disable: (id: string) => Promise<StrategyActionResult>;
    discard: (id: string) => Promise<StrategyActionResult>;
    archive: (id: string) => Promise<StrategyActionResult>;
    unarchive: (id: string) => Promise<StrategyActionResult>;
};

type Props = {
    // The strategy the sheet acts on; null hides the sheet. The parent keeps this set while
    // a follow-up confirm dialog is open and clears it via onClose.
    strategy: Strategy | null;
    onClose: () => void;
    // Present on the list (row menu); the detail screen omits it.
    onView?: (strategy: Strategy) => void;
    onClone: (strategy: Strategy) => void;
    actions: StrategyActions;
    // Archived-view context fallback; the payload's own `archived` flag takes precedence.
    archived?: boolean;
    // Toast feedback: already-translated text.
    notify: (text: string, isError: boolean) => void;
};

type PendingConfirm = 'enable' | 'disable' | 'discard' | 'archive' | null;

const CONFIRM_COPY: Record<Exclude<PendingConfirm, null>, { title: string; message: string; destructive: boolean }> = {
    enable: { title: 'ai-strategy-action-enable', message: 'ai-strategy-enable-confirm', destructive: false },
    disable: { title: 'ai-strategy-action-disable', message: 'ai-strategy-disable-confirm', destructive: false },
    discard: { title: 'ai-strategy-action-discard', message: 'ai-strategy-discard-confirm', destructive: true },
    archive: { title: 'ai-strategy-action-archive', message: 'ai-strategy-archive-confirm', destructive: false },
};

/**
 * Contextual "..." actions for one strategy — shared by the list rows and the detail header.
 * Which actions show follows the lifecycle predicates; enable/disable/discard/archive go
 * through a confirm dialog. Results surface via `notify`.
 */
export default function QuickActionsSheet({ strategy, onClose, onView, onClone, actions, archived, notify }: Props) {
    const symbolsMap = useAppSelector(symbolsSelector);
    const [confirm, setConfirm] = useState<PendingConfirm>(null);
    const [busy, setBusy] = useState(false);
    const { consentLabel, recordConsent } = useStrategyConsent();

    if (!strategy) {
        return null;
    }

    const status = strategy.status;
    // Prefer the payload's own flag (deployed 2026-07-12); the archived-view context prop
    // remains as a fallback.
    const isArchived = strategy.archived ?? archived ?? false;
    const canClone = apiVersionState(strategy.api_version) === 'current';

    const closeAll = () => {
        setConfirm(null);
        onClose();
    };

    const notifyResult = (result: StrategyActionResult) => {
        if (result.ok) {
            notify(i18n.t('ai-strategy-updated'), false);
        } else {
            notify(result.error ?? 'Action failed', true);
        }
    };

    const runAction = async (kind: Exclude<PendingConfirm, null> | 'unarchive') => {
        if (kind === 'enable') {
            await recordConsent();
        }
        setBusy(true);
        const result = await actions[kind](strategy.id);
        setBusy(false);
        notifyResult(result);
        closeAll();
    };

    return (
        <>
            <AppBottomSheet
                visible={!confirm}
                onClose={onClose}
                title={strategyMarketLine(
                    strategy,
                    symbolsMap[strategy.instrument_code]?.nameTranslated ?? strategy.instrument_code
                )}
            >
                {onView ? <SheetAction label="ai-strategy-action-view" onPress={() => onView(strategy)} /> : null}
                {/* Enable covers done/seen and re-enabling a disabled strategy (backend fix
                    deployed 2026-07-12 per the lifecycle PDF). */}
                {isEnableable(status) || status === 'disabled' ? (
                    <SheetAction label="ai-strategy-action-enable" onPress={() => setConfirm('enable')} />
                ) : null}
                {isDisableable(status) ? (
                    <SheetAction label="ai-strategy-action-disable" onPress={() => setConfirm('disable')} />
                ) : null}
                {canClone ? (
                    <SheetAction
                        label="ai-strategy-action-clone"
                        onPress={() => {
                            onClose();
                            onClone(strategy);
                        }}
                    />
                ) : null}
                {isDiscardable(status) ? (
                    <SheetAction label="ai-strategy-action-discard" destructive onPress={() => setConfirm('discard')} />
                ) : null}
                {/* Archive is housekeeping, not a decision: hidden while a result still awaits
                    enable/discard (done/seen), and swapped for Unarchive in the archived view. */}
                {isArchived ? (
                    <SheetAction
                        label="ai-strategy-action-unarchive"
                        onPress={() => (busy ? undefined : runAction('unarchive'))}
                    />
                ) : isArchivable(status) && !isEnableable(status) ? (
                    <SheetAction label="ai-strategy-action-archive" onPress={() => setConfirm('archive')} />
                ) : null}
            </AppBottomSheet>

            {confirm ? (
                <ConfirmDialog
                    visible
                    title={CONFIRM_COPY[confirm].title}
                    message={CONFIRM_COPY[confirm].message}
                    destructive={CONFIRM_COPY[confirm].destructive}
                    consentLabel={confirm === 'enable' ? consentLabel : undefined}
                    onConfirm={() => (busy ? undefined : runAction(confirm))}
                    onCancel={closeAll}
                />
            ) : null}
        </>
    );
}
